const REDACTED = '[redacted]';

/** Allowlist: header values not listed here are redacted. A denylist would miss opaque session creds like `x-s`. */
const SAFE_HEADER_NAMES = new Set([
  'accept',
  'accept-encoding',
  'accept-language',
  'cache-control',
  'content-length',
  'content-type',
  'date',
  'etag',
  'user-agent',
  'vary',
  'x-amzn-trace-id',
  'x-logrocket-url',
  'x-request-id',
  'x-version',
  'x-vo',
]);

/** Query-param names whose value is stripped from recorded URLs (LogRocket captures page URLs natively). */
const SENSITIVE_QUERY_PARAM_SUBSTRINGS = [
  'token',
  'code',
  'secret',
  'password',
  'auth',
  'jwt',
  'signature',
  'sig',
  'otp',
  'magic',
  'reset',
  'session',
  'key',
];

/** Path segment immediately after one of these is redacted (e.g. /reset-password/<token>). */
const SENSITIVE_PATH_PREFIXES = ['reset-password'];

export function buildLogRocketTraits(params: {
  browserTrackerId: string;
  isAdmin: boolean;
  isShef: boolean;
}): Record<string, string | number | boolean> {
  return {
    btId: params.browserTrackerId,
    is_admin: params.isAdmin,
    is_shef: params.isShef,
  };
}

export function isSafeHeader(name: string): boolean {
  return SAFE_HEADER_NAMES.has(name.toLowerCase());
}

export function redactHeaders(
  headers: Record<string, string | null | undefined>
): Record<string, string | null | undefined> {
  return Object.fromEntries(Object.entries(headers).map(([key, value]) => [key, isSafeHeader(key) ? value : REDACTED]));
}

function tryParseUrl(raw: string): URL | null {
  try {
    return new URL(raw);
  } catch {
    return null;
  }
}

export function sanitizeUrl(rawUrl: string): string {
  const url = tryParseUrl(rawUrl);
  if (url === null) {
    return rawUrl;
  }
  const sensitiveKeys = Array.from(url.searchParams.keys()).filter((key) =>
    SENSITIVE_QUERY_PARAM_SUBSTRINGS.some((substring) => key.toLowerCase().includes(substring))
  );
  sensitiveKeys.forEach((key) => url.searchParams.set(key, REDACTED));
  const segments = url.pathname.split('/');
  url.pathname = segments
    .map((segment, i) =>
      i > 0 && segment && SENSITIVE_PATH_PREFIXES.includes(segments[i - 1].toLowerCase()) ? REDACTED : segment
    )
    .join('/');
  return url.toString();
}
